import React, { useCallback, useEffect, useMemo } from 'react';
import { Resizable } from 're-resizable';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import {
  SidePanelModes,
  changePanelContent,
  changeSidepanelDimensions,
  openSidePanel,
} from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import theme from 'src/config/theme';
import { Typography } from '@material-ui/core';
import { FontAwesomeSpriteButton } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { switchDebugMode } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import { currentEnv } from 'src';
import { lazyWithRetry } from 'src/modules/shared/helpers/lazyRetry';

import {
  Handler,
  SidepanelContentWrapper,
  SidepanelHeader,
  SidePanelNav,
  SidepanelNavBar,
  style,
} from './Atoms';
import { NotificationTabMenu } from '../Notifications/TabMenu/NotificationsTabMenu';
import { Notifications } from '../Notifications/Notifications';

const GeoGebraCalculator = lazyWithRetry(
  () => import('src/modules/trajectory/components/TrajectoryViewDetail/GeoGebraCalculator')
);
const AddStudentsSidebarContent = lazyWithRetry(
  () => import('src/modules/groups/components/addGroup/AddStudentsSidebarContent')
);
const FroalaNotesEditor = lazyWithRetry(
  () => import('src/modules/notes/components/FroalaNotesEditor')
);
const TeacherManual = lazyWithRetry(() => import('src/modules/manuals/components/TeacherManual'));
const TrajectorySidebar = lazyWithRetry(
  () =>
    import('src/modules/trajectory/components/TrajectoryDetail/TrajectorySidebar/TrajectorySidebar')
);
const ScoringViewPageSidebar = lazyWithRetry(
  () =>
    import('src/modules/score-progression/components/ScoringViewPageSidebar/ScoringViewPageSidebar')
);
const GroupNotesList = lazyWithRetry(() => import('src/modules/notes/components/GroupNotesList'));
const TrainerForm = lazyWithRetry(
  () => import('src/modules/study-card/components/trainer-form/TrainerForm')
);
const TeacherBookmarkSidebarContent = lazyWithRetry(
  () =>
    import(
      'src/modules/trajectory/components/TrajectoryDetail/TrajectorySidebar/TeacherBookmarkSidebarContent'
    )
);

type ComponentMap = {
  [key in SidePanelModes]: { component: JSX.Element; navbar: JSX.Element };
};

const componentMap: ComponentMap = {
  [SidePanelModes.GEOGEBRA]: { component: <GeoGebraCalculator />, navbar: <></> },
  [SidePanelModes.GROUPS]: { component: <AddStudentsSidebarContent />, navbar: <></> },
  [SidePanelModes.NOTES]: { component: <FroalaNotesEditor />, navbar: <></> },
  [SidePanelModes.NONE]: { component: <></>, navbar: <></> },
  [SidePanelModes.MANUAL]: { component: <TeacherManual />, navbar: <></> },
  [SidePanelModes.DIFFERENTIATION]: { component: <TrajectorySidebar />, navbar: <></> },
  [SidePanelModes.DASHBOARD]: { component: <ScoringViewPageSidebar />, navbar: <></> },
  [SidePanelModes.GROUP_NOTES]: { component: <GroupNotesList />, navbar: <></> },
  [SidePanelModes.TRAINER_FORM]: { component: <TrainerForm />, navbar: <></> },
  [SidePanelModes.TEACHER_BOOKMARK]: {
    component: <TeacherBookmarkSidebarContent />,
    navbar: <></>,
  },
  [SidePanelModes.NEWS]: { component: <Notifications />, navbar: <NotificationTabMenu /> },
};

function getTitle(mode: SidePanelModes | undefined, calcType?: string) {
  let title = 'Title';
  switch (mode) {
    case 'notes':
    case 'groupnotes':
      title = 'Notities';
      break;
    case 'groups':
      title = 'Selecteer leerlingen';
      break;
    case 'differentiation':
      title = 'Trajectversies';
      break;
    case 'teacher_bookmark':
      title = 'Bladwijzer';
      break;
    case 'dashboard':
      title = 'Legende';
      break;
    case 'manual':
      title = 'Leerkrachtenhandleiding';
      break;
    case 'news':
      title = 'Berichten';
      break;

    case 'geogebra':
      switch (calcType) {
        case 'GEOGEBRA_SCIENTIFIC':
          title = 'Wetenschappelijk rekentoestel';
          break;
        case 'GEOGEBRA_GRAPHING':
          title = 'Grafisch rekentoestel';
          break;
        case 'GEOGEBRA_CAS':
          title = 'Algebraïsch rekentoestel';
          break;
        default:
          break;
      }
      break;
    case SidePanelModes.TRAINER_FORM:
      title = 'Trainen';
      break;

    default:
      break;
  }
  return title;
}

export const SliderPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const sidePanelMode = useAppSelector((state) => state.sidepanel.sidePanelMode);
  const sidepanel = useAppSelector((state) => state.sidepanel.sidepanel);
  const isOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const geogebraAppName = useAppSelector((state) => state.geogebra.geogebraAppName);
  const currentTrajectoryId = useAppSelector((state) => state.trajectories.currentTrajectoryId);
  const blocksDebugMode = useAppSelector((state) => state.blocks.debugMode);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );

  function vwToPx(): number {
    // Get the width of the viewport in pixels
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    // Calculate the pixel value
    const pxValue = (50 * viewportWidth) / 100 - theme.config.trajectoryToolboxWidth;

    return pxValue;
  }

  const content = useMemo(() => {
    if (!isOpen) return null;
    return (sidePanelMode && componentMap[sidePanelMode].component) || null;
  }, [sidePanelMode, isOpen]);

  useEffect(() => {
    dispatch(openSidePanel(false));
  }, [dispatch]);

  useEffect(() => {
    if (sidePanelMode === 'none') {
      dispatch(openSidePanel(false));
    }
  }, [dispatch, sidePanelMode]);

  const handleCloseSliderPanel = useCallback(() => {
    if (SidePanelModes.MANUAL) {
      log(LogActionTypes.PAPER_MANUAL_CLOSE, {
        trajectoryId: currentTrajectoryId,
        type: 'slider.painel.button',
      });
    }
    dispatch(changePanelContent(SidePanelModes.NONE));
    dispatch(openSidePanel(false));
  }, [currentTrajectoryId, dispatch, log]);

  const handleSwitchDebugMode = useCallback(() => {
    dispatch(switchDebugMode());
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <Resizable
      maxWidth={vwToPx()}
      minHeight={'100%'}
      minWidth={isOpen ? '320px' : 0}
      size={{
        width: sidepanel?.width as number,
        height: '100%',
      }}
      onResizeStop={(e, direction, ref, d) => {
        dispatch(
          changeSidepanelDimensions({ ...sidepanel, width: (sidepanel?.width as number) + d.width })
        );
      }}
      enable={{ left: true }}
      style={{ ...style, right: isOpen ? 0 : '-50dvw' }}
    >
      <SidePanelNav width={sidepanel?.width} className="sidepanel">
        <SidepanelHeader width={sidepanel?.width} presentationmode={showPresentationMode}>
          <Typography variant="h2">{getTitle(sidePanelMode, geogebraAppName)}</Typography>
          <div>
            {currentEnv === 'local' && (
              <FontAwesomeSpriteButton
                icon={SpriteIconsLight.EYE}
                iconSize="1x"
                color={blocksDebugMode ? theme.palette.primary.main : theme.palette.common.black}
                onClick={() => {
                  handleSwitchDebugMode();
                }}
              />
            )}
            <FontAwesomeSpriteButton
              icon={SpriteIconsLight.XMARK}
              iconSize="1x"
              color={theme.palette.common.black}
              onClick={() => {
                handleCloseSliderPanel();
              }}
            />
          </div>
        </SidepanelHeader>
        {!showPresentationMode && (
          <SidepanelNavBar width={sidepanel?.width}>
            {sidePanelMode && componentMap[sidePanelMode].navbar}
          </SidepanelNavBar>
        )}
      </SidePanelNav>
      <Handler />
      <SidepanelContentWrapper width={sidepanel?.width}>{content}</SidepanelContentWrapper>
    </Resizable>
  );
};

export default SliderPanel;
